import { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import {
  getInvoiceUrl,
  Invoice,
  InvoiceStatus,
  useCurrentOrganizationId,
  useInvoice,
  useMe,
} from '@app/api';
import { useLocale } from '@app/i18n';
import { BackButton, Button, InformationBox, Link, NotFound } from 'components';
import { InvoiceDetails } from './InvoiceDetails';
import { InvoicePaymentForm } from './InvoicePaymentForm';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    minHeight: '100%',
    [theme.media.tablet]: {
      gridTemplateColumns: '1fr',
    },
  },
  invoicePanel: {
    position: 'relative',
    flex: 1,
    backgroundColor: theme.colors.grayBackground,
    display: 'flex',
    justifyContent: 'center',
  },
  back: {
    position: 'fixed',
    top: theme.marginBase * 4,
    left: theme.marginBase * 4,
  },
  invoiceLayout: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    [theme.media.tablet]: {
      position: 'relative',
    },

    backgroundColor: theme.colors.grayBackground,
    padding: [[theme.marginBase * 8, theme.marginBase * 6]],
    overflowY: 'auto',
  },
  invoiceContent: {
    minWidth: 520,
    [theme.media.tablet]: {
      minWidth: 'auto',
    },
  },
  formPanel: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  formContent: {
    maxWidth: theme.maxContentWidth.largeForm,
    width: '100%',
    padding: [[theme.marginBase * 4, theme.marginBase * 6]],

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',
    minHeight: '100%',
  },
  rightTitle: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 4,
    marginTop: theme.marginBase * 2,
  },
  invoiceLink: {
    marginTop: theme.marginBase * 2,
    display: 'block',
  },
  buttonsLine: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase * 4,
  },
}));

export const InvoicePage = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { id: invoiceId } = useParams<{ id: string }>();

  const { data: me } = useMe(true);
  useLocale(me?.locale);
  const logged = !!me;

  const [paidInvoice, setPaidInvoice] = useState<Invoice | null>(null);

  const { data: fetchedInvoice, isLoading } = useInvoice(
    logged ? invoiceId : undefined,
  );
  const invoice = paidInvoice || fetchedInvoice;

  const currentOrganizationId = useCurrentOrganizationId();
  const organizationId = invoice?.organizationId || currentOrganizationId;

  const goToDashboard = useCallback(
    () => navigate('/billing-dashboard'),
    [navigate],
  );

  const invoiceUrl = getInvoiceUrl(invoice);

  const renderRightPanel = () => {
    if (!invoice || !organizationId) {
      return null;
    }
    if (paidInvoice) {
      return (
        <div>
          <InformationBox
            title={t('billing.payInvoice.paidTitle')}
            text={t('billing.payInvoice.paidContent')}
            variant="success"
          />
          <div className={classes.buttonsLine}>
            <Button
              text={t('billing.payInvoice.backToDashboard')}
              onClick={goToDashboard}
            />
          </div>
        </div>
      );
    }

    if (invoice && invoice.status !== InvoiceStatus.OPEN) {
      return (
        <div>
          <InformationBox
            text={t(`billing.payInvoice.notPayable.${invoice.status}`, {
              defaultValue: t('billing.payInvoice.notPayable.default'),
            })}
            variant={
              invoice.status === InvoiceStatus.PAID ? 'success' : 'danger'
            }
          />
          {!!invoiceUrl && (
            <Link
              href={invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.invoiceLink}
              isExternalLink
            >
              {t('billing.invoice.see')}
            </Link>
          )}
          <div className={classes.buttonsLine}>
            <Button
              text={t('billing.payInvoice.backToDashboard')}
              onClick={goToDashboard}
            />
          </div>
        </div>
      );
    }

    return <InvoicePaymentForm invoice={invoice} onSuccess={setPaidInvoice} />;
  };

  if (!invoice && !isLoading) {
    return <NotFound />;
  }

  return (
    <div className={classes.container}>
      <div className={classes.invoicePanel}>
        <BackButton
          color="ink"
          className={classes.back}
          to="/settings/billing"
        />
        <div className={classes.invoiceLayout}>
          <div className={classes.invoiceContent}>
            <InvoiceDetails invoice={invoice} />
          </div>
        </div>
      </div>
      <div className={classes.formPanel}>
        <div className={classes.formContent}>{renderRightPanel()}</div>
      </div>
    </div>
  );
};
